// General
import "./payment-method-selection.scss";
import { useEffect } from "react";
// Services
import { useLazyGetPaymentGatewayListQuery } from "../../../../services/data.service";
// Redux
import { useSelector, useDispatch } from "react-redux";
import {
  updatePaymentMethodSelection,
  updateIsRedirect,
  updateIsFormValid,
} from "../../../../redux/store/paymentStore";
// react-gtm-module
import TagManager from "react-gtm-module";
// Material UI
import { FormControl, Select, MenuItem } from "@mui/material";
// i18next
import { useTranslation } from "react-i18next";
// Custom Hooks
import IconManager from "../../../utility/manager/icon-manager/icon-manager";
// Components
import Spinner from "../../elements/spinner/spinner";

const PaymentMethodSelection = (props) => {
  const { availableGateways, xenopayMethodId, onXenopayMethodChange } = props;

  // API variables
  const [
    getPaymentGatewayList,
    {
      data: paymentGatewayListData,
      error: paymentGatewayListErrorData,
      isFetching: paymentGatewayListFetching,
      isLoading: paymentGatewayListLoading,
      isSuccess: paymentGatewayListSuccess,
      isError: paymentGatewayListError,
    },
  ] = useLazyGetPaymentGatewayListQuery();

  // Redux variables
  const paymentMethodSelection = useSelector(
    (state) => state.payment.paymentMethodSelection
  );
  const dispatch = useDispatch();

  // i18next variables
  const { t } = useTranslation();

  // Custom Hooks Functions
  const getIcon = IconManager();

  // Lifecycle | Mounted
  useEffect(() => {
    getPaymentGatewayList("?product_type=coin", false);
  }, []);

  // Event Handlers | Button
  const onSelectPaymentProvider = (provider) => {
    dispatch(updatePaymentMethodSelection(provider?.provider));
    dispatch(updateIsRedirect(provider?.provider_action === "redirect"));

    if (provider?.provider === "xenopay") {
      dispatch(updateIsFormValid(true));

      TagManager.dataLayer({
        dataLayer: {
          event: "PWA-S1-1.1",
        },
      });
    } else if (provider?.provider === "alphapo") {
      dispatch(updateIsFormValid(true));

      // TagManager.dataLayer({
      //   dataLayer: {
      //     event: "PWA-S1-1.2",
      //   },
      // });
    } else if (provider?.provider === "genesis") {
      dispatch(updateIsFormValid(true));

      // TagManager.dataLayer({
      //   dataLayer: {
      //     event: "PWA-S1-1.3",
      //   },
      // });
    } else if (provider?.provider === "cardpay") {
      dispatch(updateIsFormValid(true));

      // TagManager.dataLayer({
      //   dataLayer: {
      //     event: "PWA-S1-1.4",
      //   },
      // });
    }
  };

  // Utility Functions
  const getPaymentLogo = (logo) => {
    switch (logo) {
      case "lock":
        return getIcon("creditCardIcon", "lock-icon");
      case "fpx":
        return getIcon("fpxLogo", "fpx-icon");
      case "crypto":
        return getIcon("cryptocurrencyIcon", "cryptocurrency-icon");
      default:
        return getIcon("paymentSecureLockIcon", "lock-icon");
    }
  };

  if (paymentGatewayListFetching || paymentGatewayListLoading) {
    return <Spinner />;
  } else if (paymentGatewayListSuccess) {
    return (
      <div id="payment-method-selection-subcomponent">
        <div className="select-your-payment-label">
          {t("shop.select_your_payment")}
        </div>

        <div className="payment-secure-logo-container">
          {getIcon("discreteBadge", "discrete-badge")}
          {getIcon("securityBadge", "security-badge")}
          {getIcon("sslBadge", "ssl-badge")}
          {getIcon("payWithCryptoBadge", "pay-with-crypto-badge")}
        </div>

        {paymentGatewayListData?.data?.payment_methods?.map(
          (paymentGateway, index) => {
            if (!availableGateways?.includes(paymentGateway?.provider))
              return null;

            return (
              <div
                className={`payment-method-container ${
                  paymentMethodSelection === paymentGateway?.provider
                    ? "selected-payment-provider"
                    : ""
                }`}
                key={index}
                onClick={() => onSelectPaymentProvider(paymentGateway)}
              >
                <div className="payment-icon-container">
                  {getPaymentLogo(paymentGateway?.icon)}
                </div>

                <div className="payment-description-container">
                  <div className="payment-title-container">
                    <div className="payment-title">{paymentGateway?.title}</div>
                    {paymentGateway?.last_used && (
                      <div className="last-used-label">
                        {t("common.last_used")}
                      </div>
                    )}
                  </div>

                  <div className="payment-description">
                    {paymentGateway?.paragraph}
                  </div>

                  {paymentGateway?.payment_icons?.length > 0 && (
                    <div className="sub-payment-icons-container">
                      {paymentGateway?.payment_icons?.map(
                        (paymentIcon, index) => (
                          <div
                            className="sub-payment-icon-container"
                            key={index}
                          >
                            <img
                              className="sub-payment-icon"
                              src={paymentIcon?.logo}
                              alt={paymentIcon?.title}
                            />
                          </div>
                        )
                      )}
                    </div>
                  )}

                  {paymentGateway?.provider === "xenopay" &&
                    paymentMethodSelection === "xenopay" &&
                    paymentGateway?.payment_methods?.length > 0 && (
                      // Stop clicks (incl. the portaled menu) from re-selecting the provider
                      <FormControl
                        className="xenopay-method-select custom-text-field"
                        variant="filled"
                        size="small"
                        hiddenLabel
                        fullWidth
                        onClick={(event) => event.stopPropagation()}
                      >
                        <Select
                          displayEmpty
                          MenuProps={{ PaperProps: { sx: { maxHeight: 300 } } }}
                          value={xenopayMethodId}
                          onChange={(event) =>
                            onXenopayMethodChange(event.target.value)
                          }
                        >
                          <MenuItem disabled value="">
                            <em>{t("shop.select_your_payment")}</em>
                          </MenuItem>

                          {paymentGateway?.payment_methods?.map((method) => (
                            <MenuItem key={method?.id} value={method?.id}>
                              {method?.name}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    )}
                </div>

                <div className="select-container">
                  <div className="select-payment-container">
                    {paymentMethodSelection === paymentGateway?.provider && (
                      <div className="selected-payment-inner"></div>
                    )}
                  </div>
                </div>
              </div>
            );
          }
        )}
      </div>
    );
  } else if (paymentGatewayListError) {
  }
};

export default PaymentMethodSelection;
